<template>
  <div class="multiple-users">
    <h2>Multiple Users</h2>
    <p class="item">
      For each tier plan, set the maximum number of users the contractor will be able to add
      to their account.
    </p>

    <ul class="item" v-if="!formIsActive">
      <li v-for="(item, index) in this.setting" :key="index">
        {{ item.name }}
        ({{ item.value }})<template v-if="setting.length > index + 1">,</template>
      </li>
    </ul>

    <div class="item form" v-if="formIsActive">
      <div class="table-box">
        <b-table
            :data="formSetting"
            :default-sort="['value', 'ASC']"
            :mobile-cards="false"
        >
          <template v-slot="props">
            <b-table-column field="name" label="Tier" width="250" cell-class="table-cell">
              {{ props.row.name }}
            </b-table-column>
            <b-table-column
                field="value"
                label="Multiple users"
                width="150"
                cell-class="table-cell"
                centered
            >
              <b-select v-model="props.row.value">
                <option
                    v-for="(value, index) in formOptions"
                    :value="value"
                    :key="index"
                >
                  {{ value }}
                </option>
              </b-select>
            </b-table-column>
          </template>
        </b-table>
      </div>
      <div class="buttons">
        <b-button
            class="button is-success is-outlined"
            @click="onFormCancel"
        >
          cancel
        </b-button>

        <b-button
            class="button is-success"
            @click="() => onFormSubmit()"
        >
          submit
        </b-button>
      </div>
    </div>

    <template
        v-if="
          !formIsActive
          && hasAccess(MANAGE_SETTINGS_MODIFY, MANAGE_MULTIPLE_USERS_MODIFY, true)
        "
    >
      <div class="item">
        <b-button
            class="button is-success edit-btn"
            @click="onEdit"
        >
          edit
        </b-button>
      </div>
    </template>

    <div class="item">
      <span class="settings-text--gray">Last updated: {{ lastUpdated }}</span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import * as MUTATIONS from '@/store/mutations';
import { getMultipleUsers, setMultipleUsers } from '@/api/settings';
import MultipleUsersConfirmation from '@/components/Modal/MultipleUsersConfirmation.vue';
import Vue from 'vue';
import { CUSTOMER_MAX_ALLOWED_MAX_TEAM_USERS } from '@/const/constants';

export default {
  name: 'MultipleUsers',
  props: {
    data: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters(['hasAccess']),
  },
  data() {
    return {
      setting: [],
      formSetting: [],
      formOptions: [...Array(CUSTOMER_MAX_ALLOWED_MAX_TEAM_USERS + 1).keys()],
      lastUpdated: null,
      formIsActive: false,
      confirmModal: null,
      MANAGE_SETTINGS_MODIFY: { resource: 'manage-settings', code: 'modify' },
      MANAGE_MULTIPLE_USERS_MODIFY: { resource: 'manage-multiple-users', code: 'modify' },
    };
  },
  async mounted() {
    await this.loadSetting();
  },
  methods: {
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),
    setSettingDataFromResponse(response) {
      const setting = response.data.data;
      const settingValue = JSON.parse(setting.paramVal);

      const sortedSetting = [];
      Object.entries(settingValue).forEach(([name, value]) => {
        sortedSetting.push({ name, value });
      });
      sortedSetting.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

      this.setting = JSON.parse(JSON.stringify(sortedSetting));
      this.formSetting = JSON.parse(JSON.stringify(sortedSetting));

      let lastEditText = '';

      if (setting.email) {
        lastEditText += setting.email;
      }

      if (setting.lastEdit) {
        const date = new Date(setting.lastEdit);
        if (setting.email) {
          lastEditText += ', ';
        }
        lastEditText += date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }

      this.lastUpdated = lastEditText;
    },
    async loadSetting() {
      try {
        const response = await getMultipleUsers();
        this.setSettingDataFromResponse(response);
      } catch (error) {
        this.$buefy.snackbar.open({
          message: 'Something wrong',
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }
    },
    onEdit() {
      this.formIsActive = true;
    },
    onFormSubmit(confirmationReceived = false) {
      const payload = {
        multipleUsers: {},
        confirmationReceived,
      };

      this.formSetting.forEach((item) => {
        payload.multipleUsers[item.name] = item.value;
      });

      this.startLoading();
      setMultipleUsers(payload)
        .then((response) => {
          if (response.data.confirmationRequired) {
            this.openConfirmModal(response.data.contractors);
          } else {
            this.setSettingDataFromResponse(response);
            this.formIsActive = false;

            if (confirmationReceived) {
              this.confirmModal.$off('close', this.onConfirmClose);
              this.confirmModal.close();
              this.confirmModal = null;
            }

            this.$buefy.toast.open({ message: 'Update successful!', duration: 3000, type: 'is-success' });
          }
        })
        .catch(() => {
          this.$buefy.snackbar.open({
            message: 'Something wrong',
            actionText: ' ',
            position: 'is-top',
            indefinite: true,
            type: 'is-danger',
          });
        })
        .finally(() => {
          this.finishLoading();
        });
    },
    openConfirmModal(contractors = []) {
      const Component = Vue.extend(MultipleUsersConfirmation);
      this.confirmModal = this.$buefy.modal.open({
        parent: this,
        props: {
          contractors,
        },
        component: Component,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'multiple-users-confirmation card-form-modal',
        canCancel: ['outside'],
        events: {
          confirm: () => {
            this.onFormSubmit(true);
          },
          cancel: () => {
            this.confirmModal.close();
          },
        },
      });
      this.confirmModal.$on('close', this.onConfirmClose);
    },
    onConfirmClose() {
      if (this.confirmModal) {
        this.confirmModal.$off('close', this.onConfirmClose);
        this.confirmModal = null;
      }
    },
    onFormCancel() {
      this.formSetting = JSON.parse(JSON.stringify(this.setting));
      this.formIsActive = false;
    },
  },

};
</script>

<style lang="stylus" scoped>
ul
  display flex
  li:not(:last-child)::after
    content " "
    white-space pre

.item
  margin 12px 0

.edit-btn.button
  min-width 110px
.edit-btn.button.is-success
  border 1px solid #68BA62
.button.button.is-success:hover
  background-color #17B548
  border 1px solid #17B548
  color white

.buttons
  margin-left: 40px
  @media screen and (max-width 850px)
    width 100%
    margin-left: 0
    margin-top: 40px
.button
  height 48px
  min-width 120px
  text-transform uppercase

.form
  display flex
  flex-wrap: wrap
  align-items start

.table-box
  min-width 403px
  @media screen and (max-width 850px)
    min-width 100%

>>> .table td,
>>> .table th
  padding 10px 20px
  font-weight 400 !important

>>> .table td
  font-size 16px
  border-left: 1px solid rgba(143,167,179,0.3)
  border-right: 1px solid rgba(143,167,179,0.3)

>>> .b-table .table
  border-bottom: 1px solid rgba(143,167,179,0.3)

>>> .b-table .table-wrapper tr:first-child th
  background-color #1aab48
  color white
  border-right 1px solid #81d49d
  font-size 12.8px

>>> .b-table td:nth-child(odd)
  background-color: #f2f5f6;
>>> .b-table td:nth-child(even)
  background-color: white;

>>> .select,
>>> select
  width 100%
</style>
